import {
  Autocomplete as BaseAutocomplete,
  type AutocompletePositionerProps,
  type AutocompleteRootChangeEventDetails,
} from "@base-ui/react/autocomplete";
import classNames from "classnames";
import * as React from "react";

import { Field } from "../Field";
import popupStyles from "../internal/listbox/listbox.module.css";
import {
  ListboxContext,
  type ListboxContextValue,
  type ListboxItemGroup,
  resolveItemLabel,
  useListboxContext,
} from "../internal/listbox/listboxContext";
import {
  Footer,
  type ListboxFooterProps,
  type ListboxMatchTextProps,
  type ListboxUseTypedProps,
  MatchText,
  UseTyped,
} from "../internal/listbox/listboxParts";
import { TextInput } from "../TextInput";

import styles from "./Autocomplete.module.css";

/** Control density of the input, on the same scale as `TextInput` and `Combobox`. */
export type AutocompleteSize = "sm" | "md" | "lg";

/**
 * Details accompanying an input edit, most usefully `reason`, which says what
 * caused it (`"input-change"` for typing, `"item-press"` for a picked row).
 * Re-exported so a consumer can type a named handler without importing from
 * `@base-ui/react`.
 */
export type AutocompleteChangeEventDetails = AutocompleteRootChangeEventDetails;

/**
 * Match test for one item against the typed text. The third argument is the
 * root's `itemToStringValue`, so a rule can match the text a row stands for.
 */
export type AutocompleteFilter<T> = (
  item: T,
  query: string,
  itemToString?: (item: T) => string,
) => boolean;

/**
 * One labeled run of items. Base UI's own shape: only `items` is read, and any
 * other field (a label, an id) rides along to the `List` render function.
 */
export type AutocompleteItemGroup<T> = ListboxItemGroup<T>;

/** What the input needs from the root to open and close the popup itself. */
interface AutocompleteInputContextValue {
  /** Opens the popup if the root asks for it on focus (see `openOnFocus`). */
  openFromFocus: () => void;
  isOpen: boolean;
  close: () => void;
}

const AutocompleteInputContext =
  React.createContext<AutocompleteInputContextValue>({
    openFromFocus: () => {},
    isOpen: false,
    close: () => {},
  });

export interface AutocompleteRootProps<T> {
  /**
   * The suggestions, in the order they should be offered.
   *
   * Pass groups instead to offer them under labels; `List` then renders one
   * `Group` per entry.
   */
  items: readonly T[] | readonly AutocompleteItemGroup<T>[];

  /**
   * The text in the input, which is the field's value: suggestions fill it,
   * but any text the user types is just as valid.
   */
  value: string;

  /**
   * Fired with the next text on every edit: typed, filled in from a picked
   * row, or cleared by Escape while the popup is closed. `eventDetails.reason`
   * says which.
   */
  onValueChange: (
    value: string,
    eventDetails: AutocompleteChangeEventDetails,
  ) => void;

  /**
   * Visible label. Giving one wraps the field in a `Field`, which names the
   * input. Without one, name the input with a `Field` around the field (which
   * also describes it) or with `aria-label` on `Input`.
   */
  label?: React.ReactNode;

  /**
   * Height, padding, and font size of the input. Popup density is unaffected.
   * @default "md"
   */
  size?: AutocompleteSize;

  /**
   * Match test for one item against the typed text. Defaults to Base UI's
   * substring match on each item's text, which ignores case, accents, and
   * punctuation.
   *
   * Pass `null` when `items` already match it (a server search), so they are
   * listed as given.
   *
   * `MatchText` marks only a case-insensitive literal substring, so a row kept
   * on looser grounds (the default's accent and punctuation folding included)
   * can show no mark.
   */
  filter?: AutocompleteFilter<T> | null;

  /**
   * How an item becomes the text a picked row fills the input with, and that
   * the default filter and the marks match against.
   *
   * Provide this whenever the item type is not a string or `{ label, value }`.
   */
  itemToStringValue?: (item: T) => string;

  /**
   * Whether the popup is open. Leave it out to let the field manage it: the
   * popup opens as the user types (and on focus, with `openOnFocus`) and
   * closes on Escape, on a pick, or when focus leaves.
   */
  open?: boolean;

  /** Fired whenever the popup opens or closes, controlled or not. */
  onOpenChange?: (open: boolean) => void;

  /**
   * Open the popup as soon as the input takes focus, so the suggestions are
   * on offer before anything is typed.
   * @default false
   */
  openOnFocus?: boolean;

  /**
   * Whether the first row is highlighted as the user types, so Enter picks it
   * without arrowing to it. Off by default: Enter then keeps exactly the text
   * that was typed unless the user moved onto a row first.
   * @default false
   */
  autoHighlight?: boolean;

  /**
   * Fired whenever the highlighted row changes, with its item, or `undefined`
   * once nothing is highlighted. Besides the user arrowing or pointing, the
   * highlight moves when the list changes under it, and clears when the popup
   * closes.
   */
  onItemHighlighted?: (item: T | undefined) => void;

  /** Whether the field ignores user interaction. */
  disabled?: boolean;

  /** Extra class(es) for the field wrapper; size the field here. */
  className?: string;

  children?: React.ReactNode;
}

/**
 * A free-text input that suggests values as the user types. Unlike `Combobox`,
 * which commits one of its items, the text in the input is the value: a
 * suggestion is a shortcut to typing it, never a gate.
 *
 * Rows are whatever `Item` renders, so a row can carry more than its text (a
 * count, a swatch). By default an `Item` shows its text with the typed text
 * marked (`MatchText`).
 *
 * Unless `autoHighlight` is on, nothing is highlighted until the user arrows
 * or points onto a row, so Enter in the input keeps the typed text and, inside
 * a form, submits it. Enter on a highlighted row picks that row without
 * submitting.
 *
 * @example
 * ```tsx
 * <Autocomplete items={versions} value={version} onValueChange={setVersion} label="Version" openOnFocus>
 *   <Autocomplete.Input placeholder="Enter value..." />
 *   <Autocomplete.Content>
 *     <Autocomplete.Empty />
 *     <Autocomplete.List>
 *       {(version: string) => <Autocomplete.Item key={version} value={version} />}
 *     </Autocomplete.List>
 *   </Autocomplete.Content>
 * </Autocomplete>
 * ```
 */
function AutocompleteRoot<T>({
  items,
  value,
  onValueChange,
  label,
  size = "md",
  filter,
  itemToStringValue,
  open,
  onOpenChange,
  openOnFocus = false,
  autoHighlight = false,
  onItemHighlighted,
  disabled = false,
  className,
  children,
}: AutocompleteRootProps<T>) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const isOpen = open ?? uncontrolledOpen;
  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );
  const inputContext = React.useMemo<AutocompleteInputContextValue>(
    () => ({
      openFromFocus: () => {
        if (openOnFocus) {
          handleOpenChange(true);
        }
      },
      isOpen,
      close: () => handleOpenChange(false),
    }),
    [openOnFocus, handleOpenChange, isOpen],
  );

  const toString = React.useCallback(
    (item: T) => resolveItemLabel(item, itemToStringValue),
    [itemToStringValue],
  );

  // The typed text is both the value and the query, so the marks always claim
  // it. (`Combobox` has to blank its query while the input merely shows the
  // committed item; here there is no such state.) Trimmed as Base UI trims it
  // before filtering, or a stray space would leave every matching row unmarked.
  const context = React.useMemo<ListboxContextValue>(
    () => ({
      highlightQuery: value.trim(),
      size,
      toString: toString as (item: unknown) => string,
    }),
    [value, size, toString],
  );

  const field = (
    <BaseAutocomplete.Root<T>
      items={items as readonly T[]}
      value={value}
      onValueChange={onValueChange}
      open={isOpen}
      onOpenChange={handleOpenChange}
      openOnInputClick
      filter={filter}
      itemToStringValue={itemToStringValue}
      autoHighlight={autoHighlight}
      onItemHighlighted={onItemHighlighted}
      disabled={disabled}
    >
      <ListboxContext.Provider value={context}>
        <AutocompleteInputContext.Provider value={inputContext}>
          {label !== undefined && <Field.Label>{label}</Field.Label>}
          {children}
        </AutocompleteInputContext.Provider>
      </ListboxContext.Provider>
    </BaseAutocomplete.Root>
  );

  // `Field.Root` sits outside `BaseAutocomplete.Root` because the root gives
  // the input the control id of the nearest `Field` above it, and that id is
  // what the label points at. Without a `label` the wrapper is a plain `<div>`,
  // so a `Field` the caller puts around it names the input; a `Field.Root` of
  // our own would sit between the two and cut that off.
  return label !== undefined ? (
    <Field.Root className={className} disabled={disabled}>
      {field}
    </Field.Root>
  ) : (
    <div className={classNames(styles.field, className)}>{field}</div>
  );
}

/**
 * A native input's props, less those the root owns: the text (`value` on the
 * root), its id (taken from the surrounding `Field`, so the label points at
 * it), and `size` (on the root).
 */
export interface AutocompleteInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "value" | "defaultValue" | "onChange" | "size" | "id"
> {
  /**
   * `"field"` draws the input as a `TextInput`. `"bare"` renders an unstyled
   * native `<input>` for a caller that draws its own field around it with
   * `InputGroup`; style it through `className`.
   * @default "field"
   */
  variant?: "field" | "bare";
}

/**
 * The text input, by default drawn as a `TextInput` so it lines up with the
 * plain fields beside it (see `variant`). Forwards its ref to the `<input>`.
 *
 * While the popup is open, Escape closes it and goes no further, whatever the
 * popup shows. Base UI stops it only while the popup shows rows or an `Empty`,
 * so a popup showing just a status line would otherwise let Escape through to
 * a popover or dialog around the field and close that too.
 */
const Input = React.forwardRef<HTMLInputElement, AutocompleteInputProps>(
  function Input({ variant = "field", onFocus, onKeyDown, ...props }, ref) {
    const { size } = useListboxContext("Autocomplete.Input");
    const { openFromFocus, isOpen, close } = React.useContext(
      AutocompleteInputContext,
    );

    return (
      <BaseAutocomplete.Input
        {...props}
        ref={ref}
        onFocus={(event) => {
          onFocus?.(event);
          openFromFocus();
        }}
        onKeyDown={(event) => {
          onKeyDown?.(event);
          if (event.key === "Escape" && isOpen) {
            event.stopPropagation();
            close();
          }
        }}
        render={variant === "field" ? <TextInput size={size} /> : undefined}
      />
    );
  },
);

export interface AutocompleteInputGroupProps {
  /** Extra class(es) for the group: draw the field around the input here. */
  className?: string;
  /** Names the group. The `Input` inside it still needs a name of its own. */
  "aria-label"?: string;
  /** The `bare` `Input` and whatever sits beside it inside the field. */
  children?: React.ReactNode;
}

/**
 * A field drawn around a `bare` `Input` and whatever sits beside it (a tag
 * field's pills, say). The whole group counts as part of the control: the
 * popup spans its width, and a press anywhere in it that isn't itself
 * interactive keeps focus in the input and opens the popup. Renders a
 * `<div role="group">` and forwards its ref to it.
 */
const InputGroup = React.forwardRef<
  HTMLDivElement,
  AutocompleteInputGroupProps
>(function InputGroup({ className, children, ...props }, ref) {
  return (
    <BaseAutocomplete.InputGroup ref={ref} className={className} {...props}>
      {children}
    </BaseAutocomplete.InputGroup>
  );
});

export interface AutocompleteItemProps<T> {
  value: T;
  disabled?: boolean;
  className?: string;
  /**
   * Fired when the row is picked, by pointer or by Enter while it is
   * highlighted, alongside the input being filled with its text.
   */
  onClick?: () => void;
  /** Defaults to the item's text with the typed text marked. */
  children?: React.ReactNode;
}

/** One suggestion row. Picking it fills the input with its text. */
function Item<T>({
  value,
  disabled,
  className,
  onClick,
  children,
}: AutocompleteItemProps<T>) {
  const { toString } = useListboxContext("Autocomplete.Item");

  return (
    <BaseAutocomplete.Item
      value={value}
      disabled={disabled}
      onClick={onClick}
      className={classNames(popupStyles.item, className)}
    >
      {children ?? <MatchText text={toString(value)} />}
    </BaseAutocomplete.Item>
  );
}

export interface AutocompleteContentProps {
  children?: React.ReactNode;
  /**
   * Extra class(es) for the popup.
   *
   * Set `--listbox-max-height` in one to cap how tall the popup grows (default `20rem`).
   * It stays within the room between the field and the viewport edge either way.
   */
  className?: string;
  /** @default 4 */
  sideOffset?: number;
  /** @default 8 */
  collisionPadding?: AutocompletePositionerProps["collisionPadding"];
  side?: AutocompletePositionerProps["side"];
  align?: AutocompletePositionerProps["align"];
  /**
   * What the popup does when it doesn't fit on its `side`. By default it flips
   * to the other side; `{ side: "none" }` keeps it where it is, shrinking it
   * to the room there instead.
   */
  collisionAvoidance?: AutocompletePositionerProps["collisionAvoidance"];
  /** Whether suggestions are still loading, announced as `aria-busy`. */
  busy?: boolean;
}

/**
 * The floating popup. Portal, positioning, and z-index are fused in here
 * because they're system decisions, not per-call-site ones. Forwards its ref
 * to the popup surface.
 */
const Content = React.forwardRef<HTMLDivElement, AutocompleteContentProps>(
  function Content(
    {
      children,
      className,
      sideOffset = 4,
      collisionPadding = 8,
      side,
      align,
      collisionAvoidance,
      busy,
    },
    ref,
  ) {
    return (
      <BaseAutocomplete.Portal>
        <BaseAutocomplete.Positioner
          className={popupStyles.positioner}
          sideOffset={sideOffset}
          collisionPadding={collisionPadding}
          side={side}
          align={align}
          collisionAvoidance={collisionAvoidance}
        >
          <BaseAutocomplete.Popup
            ref={ref}
            className={classNames(popupStyles.popup, className)}
            aria-busy={busy}
          >
            {children}
          </BaseAutocomplete.Popup>
        </BaseAutocomplete.Positioner>
      </BaseAutocomplete.Portal>
    );
  },
);

export interface AutocompleteListProps<T> {
  /**
   * Renders one row, or one `Group` when `items` are grouped. Base UI types
   * its items as `any`, so the annotation on this parameter is what re-imposes
   * the item type on the rows.
   */
  children: (item: T) => React.ReactNode;
  className?: string;
  /**
   * Hides the list while keeping it mounted, so it stays the input's
   * `aria-controls` target while a caller's placeholder (a loading skeleton,
   * say) stands in for it.
   */
  hidden?: boolean;
}

/** The scrollable list of suggestions. */
function List<T>({ children, className, hidden }: AutocompleteListProps<T>) {
  return (
    <BaseAutocomplete.List
      className={classNames(popupStyles.list, className)}
      hidden={hidden}
    >
      {children}
    </BaseAutocomplete.List>
  );
}

export interface AutocompleteGroupProps<T> {
  /** The group's items, which the `Collection` inside it renders. */
  items: readonly T[];
  className?: string;
  children?: React.ReactNode;
}

/** A labeled run of rows. Named by the `GroupLabel` inside it. */
function Group<T>({ items, className, children }: AutocompleteGroupProps<T>) {
  return (
    <BaseAutocomplete.Group
      items={items}
      className={classNames(popupStyles.group, className)}
    >
      {children}
    </BaseAutocomplete.Group>
  );
}

export interface AutocompleteGroupLabelProps {
  className?: string;
  children?: React.ReactNode;
}

/** The heading of a `Group`. */
function GroupLabel({ className, children }: AutocompleteGroupLabelProps) {
  return (
    <BaseAutocomplete.GroupLabel
      className={classNames(popupStyles.groupLabel, className)}
    >
      {children}
    </BaseAutocomplete.GroupLabel>
  );
}

export interface AutocompleteCollectionProps<T> {
  /** Renders one row. Annotate the parameter, as with `List`. */
  children: (item: T, index: number) => React.ReactNode;
}

/** Renders the matching items of the `Group` it sits in. */
function Collection<T>({ children }: AutocompleteCollectionProps<T>) {
  return <BaseAutocomplete.Collection>{children}</BaseAutocomplete.Collection>;
}

export interface AutocompleteEmptyProps {
  /** @default "No matches" */
  children?: React.ReactNode;
  className?: string;
}

/** Shown in place of the list when it has no rows. */
function Empty({ children = "No matches", className }: AutocompleteEmptyProps) {
  return (
    <BaseAutocomplete.Empty
      className={classNames(popupStyles.empty, className)}
    >
      {children}
    </BaseAutocomplete.Empty>
  );
}

export interface AutocompleteStatusProps {
  children?: React.ReactNode;
  className?: string;
}

/**
 * A politely announced row for the state of an asynchronous list ("Searching…",
 * an error). Render `null` children rather than unmounting this: Base UI needs
 * the element to stay put for screen readers to announce the change.
 */
function Status({ children, className }: AutocompleteStatusProps) {
  return (
    <BaseAutocomplete.Status
      className={classNames(popupStyles.status, className)}
    >
      {children}
    </BaseAutocomplete.Status>
  );
}

export type AutocompleteMatchTextProps = ListboxMatchTextProps;
export type AutocompleteFooterProps = ListboxFooterProps;
export type AutocompleteUseTypedProps = ListboxUseTypedProps;

/**
 * `Object.assign` rather than a plain object literal: it keeps the root's
 * generic call signature, so `<Autocomplete items={versions}>` still infers the
 * item type while `<Autocomplete.Item>` hangs off the same name.
 */
export const Autocomplete = Object.assign(AutocompleteRoot, {
  Input,
  InputGroup,
  Content,
  List,
  Item,
  Group,
  GroupLabel,
  Collection,
  MatchText,
  Empty,
  Status,
  Footer,
  UseTyped,
});
